import { useNavigate } from "react-router-dom";
import {
  adminDashboardCards,
  registeredUsersDashboardCards,
  userDashboardCards,
} from "../../config/dashboardCardConfig";
import { getStoredItem, setStoredItem } from "../../config/storageUtils";
import { useEffect, useState } from "react";
import api from "../../api/ApiService";
import {
  installmentsTypes,
  yojanaOptions,
} from "../../config/appliedSchemesConfig";
import ContentModal from "../common/ContentModal";
import userGeneralInfoContent from "../../config/userGeneralInfoContent";
import {
  applicationStageMap,
  applicationStatusMap,
  incentiveClaimTypes,
} from "../../config/applicationStatusConfig";
import { useLanguage } from "../LanguageProvider/LanguageProvider";
import { useAuth } from "../Auth/AuthProvider";

const DashboardCard = ({ cardName = "" }) => {
  const navigate = useNavigate();
  const { userType, setUserType } = useAuth() || "user";
  const [userDetails, setUserDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [capitalSubsidyApplicationId, setCapitalSubsidyApplicationId] =
    useState(null);
  const [interestAssistanceApplicationId, setInterestAssistanceApplicationId] =
    useState(null);
  const { language, setLanguage } = useLanguage();

  const cardsByRole = {
    admin: adminDashboardCards,
    user: userDashboardCards,
    superadmin: adminDashboardCards,
  };

  const userYojana =
    yojanaOptions.find(
      (y) => y.id === userDetails?.available_incentive_scheme,
    ) || null;

  // ✅ Initial: remove Apply card for user until API confirms it should be shown
  const initialCards =
    cardName === "RegisteredUsers"
      ? registeredUsersDashboardCards
      : (cardsByRole[userType] || []).filter(
          (card) => userType !== "user" || card.to !== "/apply-incentive-claim",
        );

  const [dashboardCard, setDashboardCard] = useState([]);

  // modal state
  const [isManualViewed, setIsManualViewed] = useState(
    (userType === "user" && userDetails?.user_details?.is_checked === "1") ||
      true,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    getUserDetails();
  }, []);

  const getUserDetails = async () => {
    const resp = await api.get("/user-full-data");
    if (resp.status === 200) {
      setUserDetails(resp.data);
      setIsManualViewed(resp.data?.user_details?.is_checked === "1");
      // setUserType(resp.data?.user_details?.role || "user");
    }
  };

  useEffect(() => {
    const checkApplications = async () => {
      if (!userDetails?.user_details) return;

      if (userType !== "user" || !userDetails?.user_details?.id) {
        setDashboardCard(initialCards);
        setLoading(false);
        return;
      }
      let canApplyCapitalIncentiveFirst = false;
      let canApplyInterestAssistance = false;
      let canApplyCapitalIncentiveSecond = false;
      let canApplyInterestAssistanceSecond = false;
      let canApplyInterestAssistanceThird = false;

      try {
        const response = await api.get(`/get-user-applications`);
        const applications = response.data || [];
        canApplyCapitalIncentiveFirst =
          !applications || applications.length === 0;

        const capitalSubsidyApplication = applications.find(
          (a) =>
            String(a.invest_claim_id) ===
            incentiveClaimTypes.sreimbursementcapitalincentive.value,
        );

        const applicationData = await api.get(
          `/get-caf/${capitalSubsidyApplication.id}`,
        );

        const codDate = new Date(applicationData.data.travel_registration_date);

        setCapitalSubsidyApplicationId(
          capitalSubsidyApplication ? capitalSubsidyApplication.id : null,
        );

        const interestAssistanceApplication = applications.find(
          (a) =>
            String(a.invest_claim_id) ===
            incentiveClaimTypes.interestassistance.value,
        );

        setInterestAssistanceApplicationId(
          interestAssistanceApplication
            ? interestAssistanceApplication.id
            : null,
        );

        const capitalSubsidylogs = capitalSubsidyApplication
          ? await api.post("/user/application/logs", {
              application_id: capitalSubsidyApplication.id,
              user_id: userDetails?.user_details?.id,
            })
          : null;

        let interestAssistanceLogs = null;

        if (interestAssistanceApplication) {
          interestAssistanceLogs = interestAssistanceApplication
            ? await api.post("/user/application/logs", {
                application_id: interestAssistanceApplication.id,
                user_id: userDetails?.user_details?.id,
              })
            : null;
        }

        const now = new Date();
        const isOlderThan = (dateStr, months) => {
          if (!dateStr) return false;

          const date = new Date(dateStr);
          const now = new Date();

          // Create a comparison date by subtracting given months from "now"
          const cutoff = new Date(now);
          cutoff.setMonth(cutoff.getMonth() - months);

          return date < cutoff; // true if the date is older than N months
        };

        const isCapitalSubsidySubmitted = (logs) =>
          Array.isArray(logs?.data)
            ? logs.data.filter(
                (log) => log.status === applicationStatusMap.applied.value,
              ).length > 0
              ? true
              : false
            : false;

        // Apply for Interest Subsidy
        if (
          isOlderThan(codDate, 6) &&
          !interestAssistanceApplication &&
          isCapitalSubsidySubmitted(capitalSubsidylogs)
        ) {
          canApplyInterestAssistance = true;
        }

        const transactionResponse = capitalSubsidyApplication
          ? await api.get(`/user-transactions/${userDetails?.user_details?.id}`)
          : null;

        const transactions =
          (transactionResponse && transactionResponse?.data) || [];

        const getLatestTransaction = (subsidy, installment) =>
          transactions
            .filter(
              (t) =>
                String(t.subsidy) === subsidy &&
                String(t.installment) === installment &&
                t.status === "paid",
            )
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

        const latestCapitalSubsidyFirstInstallment = getLatestTransaction(
          incentiveClaimTypes.sreimbursementcapitalincentive.value,
          installmentsTypes.first.value,
        );
        const latestStampDutyTransaction = getLatestTransaction(
          incentiveClaimTypes.sreimbursementcapitalincentive.value,
          installmentsTypes.stampDuty.value,
        );
        const latestCapitalSubsidySecondInstallment = getLatestTransaction(
          incentiveClaimTypes.sreimbursementcapitalincentive.value,
          installmentsTypes.second.value,
        );
        const latestInterestAssistanceFirstInstallment = getLatestTransaction(
          incentiveClaimTypes.interestassistance.value,
          installmentsTypes.first.value,
        );
        const latestInterestAssistanceSecondInstallment = getLatestTransaction(
          incentiveClaimTypes.interestassistance.value,
          installmentsTypes.second.value,
        );
        const latestInterestAssistanceThirdInstallment = getLatestTransaction(
          incentiveClaimTypes.interestassistance.value,
          installmentsTypes.third.value,
        );

        const countDrafts = (logs, applicationId) =>
          Array.isArray(logs?.data)
            ? logs.data.filter(
                (log) =>
                  log.status === applicationStatusMap.draft.value &&
                  log.application_id === applicationId,
              ).length
            : 0;

        // Apply for Capital Incentive Second Installment
        if (
          latestCapitalSubsidyFirstInstallment &&
          (latestStampDutyTransaction ||
            !capitalSubsidyApplication.application_stage) &&
          isOlderThan(codDate, 12) &&
          countDrafts(capitalSubsidylogs, capitalSubsidyApplication?.id) ===
            1 &&
          !latestCapitalSubsidySecondInstallment
        ) {
          canApplyCapitalIncentiveSecond = true;
        }

        // Apply for Interest Subsidy Second Installment
        if (
          latestInterestAssistanceFirstInstallment &&
          isOlderThan(codDate, 18) &&
          !latestInterestAssistanceSecondInstallment &&
          countDrafts(
            interestAssistanceLogs,
            interestAssistanceApplication?.id,
          ) === 1
        ) {
          canApplyInterestAssistanceSecond = true;
        }

        // Apply for Interest Subsidy Third Installment
        if (
          latestInterestAssistanceSecondInstallment &&
          isOlderThan(codDate, 30) &&
          !latestInterestAssistanceThirdInstallment &&
          countDrafts(
            interestAssistanceLogs,
            interestAssistanceApplication?.id,
          ) === 2
        ) {
          canApplyInterestAssistanceThird = true;
        }
      } catch (error) {
        console.error("Error checking applications", error);
      } finally {
        setDashboardCard(() => {
          const baseCards = cardsByRole[userType] || [];
          return baseCards.filter((card) => {
            if (userType !== "user") return true;
            switch (card.title.en) {
              case "Apply New":
                return canApplyCapitalIncentiveFirst;
              case "Apply for Interest Subsidy":
                return canApplyInterestAssistance;
              case "Apply for Capital Incentive Second Installment":
                return canApplyCapitalIncentiveSecond;
              case "Apply for Interest Subsidy Second Installment":
                return canApplyInterestAssistanceSecond;
              case "Apply for Interest Subsidy Third Installment":
                return canApplyInterestAssistanceThird;
              default:
                return true;
            }
          });
        });
        setLoading(false);
      }
    };

    checkApplications();
  }, [userType, userDetails?.user_details?.id]);

  useEffect(() => {
    // ✅ Only show modal if userType === "user" and manual not viewed
    if (userType === "user" && !isManualViewed) {
      setIsModalOpen(true);
    } else {
      setIsModalOpen(false);
    }
  }, [userType, isManualViewed]);

  const setManualViewed = async () => {
    try {
      const response = await api.post(`/user/check`);
      if (response.status === true) {
        setIsManualViewed(true);

        const updatedUserDetails = {
          ...userDetails,
          user_details: {
            ...userDetails?.user_details,
            is_checked: "1",
          },
        };
        setUserDetails(updatedUserDetails);
      }
    } catch (error) {
      console.error("Failed to fetch applications:", error);
    }
  };

  const handleCardClick = (card) => {
    if (card.to) navigate(card.to);
    else handleCardAction(card.title.en);
  };

  const applyApplicationForNextInstallment = async (
    applicationId,
    remarks,
    applicationType,
    financialIncentiveClaim,
  ) => {
    try {
      const response = await api.post(
        "/categories/applications/status/change",
        {
          application_id: applicationId,
          status: applicationStatusMap.draft.value,
          remarks: remarks,
          application_stage: applicationStageMap.user.value,
          application_type: applicationType,
        },
      );
      if (response.status === 200) {
        navigate(`/incentive-claim-form/${applicationId}`);
      }
    } catch (error) {
      console.error("Failed to apply for next installment:", error);
    }
  };

  const handleCardAction = async (title) => {
    try {
      switch (title) {
        case "Apply for Interest Subsidy":
          const resp = await api.post(
            "/applications/generate-interest-scheme",
            {
              category_id: 1,
              invest_claim_id: incentiveClaimTypes.interestassistance.value,
              user_id: userDetails?.user_details?.id,
              caf_id: userDetails?.caf_id,
              capital_incentive_application_id: capitalSubsidyApplicationId,
            },
          );

          if (resp.status === 200) {
            navigate(`/incentive-claim-form/${resp.data.application.id}`);
          }

          break;

        case "Apply for Capital Incentive Second Installment":
          await applyApplicationForNextInstallment(
            capitalSubsidyApplicationId,
            "Applying for Capital Incentive Second Installment",
            installmentsTypes.second.value,
            incentiveClaimTypes.sreimbursementcapitalincentive.value,
          );

          break;

        case "Apply for Interest Subsidy Second Installment":
          await applyApplicationForNextInstallment(
            interestAssistanceApplicationId,
            "Applying for Interest Assistance Second Installment",
            installmentsTypes.second.value,
            incentiveClaimTypes.interestassistance.value,
          );

          break;

        case "Apply for Interest Subsidy Third Installment":
          await applyApplicationForNextInstallment(
            interestAssistanceApplicationId,
            "Applying for Interest Assistance Third Installment",
            installmentsTypes.third.value,
            incentiveClaimTypes.interestassistance.value,
          );

          break;

        default:
          console.log("⚙️ Unknown action:", title.en);
      }
    } catch (error) {}
  };

  if (loading) {
    return <div>{language ? "लोड हो रहा है..." : "Loading..."}</div>;
  }

  return (
    <div>
      {/* ✅ Show welcome title only for "user" role */}
      {userType === "user" && (
        <>
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">
            {`${
              language
                ? userYojana?.label_hi + " डैशबोर्ड में आपका स्वागत है"
                : "Welcome to " + userYojana?.label + " Dashboard"
            }`}
          </h2>
          <div className="w-full flex flex-wrap items-center justify-between">
            {/* Left side - User */}
            <div className="w-full flex lg:w-1/2 mb-4 px-2 flex-start items-center">
              <span className="text-xl font-semibold block mr-2">
                {language ? "उपयोगकर्ता" : "User"}:
              </span>
              <span className="text-lg font-medium">
                {userDetails?.user_details?.name}
              </span>
            </div>

            {/* Right side - CAF ID */}
            <div className="w-full flex lg:w-1/2 mb-4 px-2 text-end items-center">
              <span className="text-xl font-semibold block mr-2 w-full">
                {language ? "सीएएफ आईडी:" : "CAF ID:"}
              </span>
              <span className="text-lg font-medium">
                {userDetails?.caf_id || (language ? "उपलब्ध नहीं" : "N/A")}
              </span>
            </div>
          </div>
        </>
      )}

      <div className="flex flex-wrap">
        {dashboardCard.map((card, index) => {
          const Icon = card.icon;
          return (
            <div
              key={index}
              className="w-full lg:w-1/3 md:w-1/2 p-2 mb-4 cursor-pointer"
              onClick={() => handleCardClick(card)}
            >
              <div
                className={`flex items-center gap-4 p-5 rounded-xl shadow-md
    ${card.bgColor === "#e8f0fe" ? "bg-[#e8f0fe]" : ""}
    ${card.bgColor === "#FFE4FD" ? "bg-[#FFE4FD]" : ""}
    ${card.bgColor === "#FFDDDD" ? "bg-[#FFDDDD]" : ""}
    ${card.bgColor === "#EDFEFF" ? "bg-[#EDFEFF]" : ""}
    ${card.bgColor === "#FFE4E4" ? "bg-[#FFE4E4]" : ""}
    ${card.bgColor === "#E4ECFF" ? "bg-[#E4ECFF]" : ""}
  `}
              >
                <div
                  className={`text-white rounded-lg flex items-center justify-center shadow-md w-14 h-14
      ${card.bgInnerColor === "#1252EB" ? "bg-[#1252EB]" : ""}
      ${card.bgInnerColor === "#D800C9" ? "bg-[#D800C9]" : ""}
      ${card.bgInnerColor === "#F00505" ? "bg-[#F00505]" : ""}
      ${card.bgInnerColor === "#018F9B" ? "bg-[#018F9B]" : ""}
      ${card.bgInnerColor === "#DE1616" ? "bg-[#DE1616]" : ""}
      ${card.bgInnerColor === "#1138aa" ? "bg-[#1138aa]" : ""}
    `}
                >
                  <Icon className="w-6 h-6 text-white" />
                </div>

                <span className="text-lg font-medium text-black ml-2">
                  {language ? card.title.hi : card.title.en}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ✅ Modal shows only if userType=user */}
      <ContentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        showClose={false}
        title={
          language
            ? "सामान्य जानकारी – प्रोत्साहन योजना"
            : "General Information – Incentive Scheme"
        }
        content={
          language ? userGeneralInfoContent.hi : userGeneralInfoContent.en
        }
        showSubmit={true}
        buttonText={
          language ? "स्वीकार करें और जारी रखें" : "Accept & Continue"
        }
        onButtonClick={setManualViewed}
      />
    </div>
  );
};

export default DashboardCard;
